import { Group, Input, UnstyledButton } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { IconGridFour, IconSearch, IconUser } from "@/components/icons";
import { HelpMenu, NotificationMenu, UserMenu, UserMenuItem } from "@/components/widgets";
import { paths } from "@/config/paths";
import { useLogout, useUser } from "@/services/auth";
import { useProfile } from "@/services/profile/get-profile";
import classes from "@/assets/styles/layout/DashboardContainer.module.css";

const isDev = import.meta.env.DEV;

interface HeaderComponentProps {
  open: () => void;
}

const HeaderDesktop: FC<HeaderComponentProps> = ({ open }) => {
  const { t } = useTranslation();
  const { data: user } = useUser();
  const { data: profile } = useProfile();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { mutate: logoutMutate } = useLogout({
    onSuccess() {
      queryClient.removeQueries();
      navigate(paths.auth.login.getHref());
    },
  });

  function logout() {
    logoutMutate({});
  }

  const userMenuItems = [
    isDev && {
      label: t("header.components"),
      leftSection: <IconGridFour />,
      to: paths.app.components.getHref(),
    },
    {
      label: t("header.profile"),
      leftSection: <IconUser />,
      to: paths.app.profile.getHref(),
    },
  ].filter(Boolean) as UserMenuItem[];

  return (
    <>
      <Group className={classes.background}>
        <UnstyledButton onClick={open} disabled={false} className={classes.searchbar}>
          <Input
            w={150}
            size="sm"
            component="div"
            aria-label={t("common.search")}
            leftSection={<IconSearch size={18} fill={"var(--mantine-color-primaryDark-4)"} />}
            styles={{
              input: {
                pointerEvents: "none",
                backgroundColor: "transparent",
                border: "1px solid var(--mantine-color-primaryDark-5)",
                color: "var(--mantine-color-primaryDark-4)",
              },
            }}
          >
            {t("common.search")}
          </Input>
        </UnstyledButton>
        <HelpMenu
          email={t("header.help.email")}
          message={t("header.help.message")}
          title={t("header.help.title")}
        />
        <NotificationMenu />
        <UserMenu
          items={userMenuItems}
          logout={{
            label: t("header.logout"),
            onClick: logout,
          }}
          theme={{ label: t("header.theme.title") }}
          user={profile ?? user ?? undefined}
        />
      </Group>
    </>
  );
};

export default HeaderDesktop;
